import { Building2, MoreHorizontal, AlertTriangle } from "lucide-react";

const BADGE = `${import.meta.env.BASE_URL}images/connectid/connectid-verified-grey.svg`;

export function FivePiecesAlternating() {
  return (
    <div className="space-y-24 lg:space-y-32" data-testid="five-pieces-alternating">
      {/* ROW 1: ConnectID */}
      <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="order-2 md:order-1 h-[420px] flex items-center justify-center">
          <div className="w-[220px] h-[420px] bg-[hsl(var(--brand-bone))] border border-[hsl(var(--brand-sand))] rounded-[2rem] p-2 shadow-sm relative">
            <div className="bg-white rounded-[1.5rem] overflow-hidden border border-[hsl(var(--brand-sand))] h-full flex flex-col">
              <div className="px-4 pt-2.5 pb-1.5 flex justify-between items-center text-[10px] font-medium text-[hsl(var(--brand-charcoal))]">
                <span>9:41</span>
                <div className="flex gap-1 items-center">
                  <div className="w-3 h-2.5 bg-[hsl(var(--brand-charcoal))]"></div>
                  <div className="w-4 h-2.5 border border-[hsl(var(--brand-charcoal))] rounded-sm relative">
                    <div className="absolute right-0.5 top-0.5 bottom-0.5 left-0.5 bg-[hsl(var(--brand-charcoal))] rounded-[1px]"></div>
                  </div>
                </div>
              </div>

              <div className="flex-1 flex flex-col px-4 pb-4">
                <div className="flex justify-center mt-2 mb-3">
                  <img
                    src={BADGE}
                    alt="Verified with ConnectID"
                    width={144}
                    height={24}
                    style={{ height: 18, width: "auto" }}
                    className="shrink-0"
                  />
                </div>
                <div className="text-center mb-4">
                  <h3 className="font-semibold text-xs leading-tight">Verify who you are with your bank</h3>
                </div>

                <div className="flex items-center justify-center gap-2 mb-5">
                  <div className="w-9 h-9 bg-white border border-[hsl(var(--brand-sand))] rounded flex items-center justify-center text-[9px] font-semibold">FID</div>
                  <MoreHorizontal className="w-3.5 h-3.5 text-[hsl(var(--brand-slate))]" />
                  <div className="w-9 h-9 bg-white border border-[hsl(var(--brand-sand))] rounded flex items-center justify-center text-[9px] font-semibold">CBA</div>
                </div>

                <div className="space-y-1.5 mb-auto">
                  <p className="text-[8px] font-semibold text-[hsl(var(--brand-slate))] uppercase tracking-wider">Select Bank</p>
                  <div className="p-2 bg-white border-2 border-[hsl(var(--brand-brass))] rounded flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Building2 className="w-3 h-3 text-[hsl(var(--brand-slate))]" />
                      <span className="text-[11px] font-medium">My Bank</span>
                    </div>
                    <div className="w-3 h-3 rounded-full border-[3px] border-[hsl(var(--brand-brass))]" />
                  </div>
                  <div className="p-2 bg-white border border-[hsl(var(--brand-sand))] rounded flex items-center gap-1.5">
                    <Building2 className="w-3 h-3 text-[hsl(var(--brand-slate))]" />
                    <span className="text-[11px]">Other Bank</span>
                  </div>
                </div>

                <button className="w-full py-2 bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] text-[11px] font-medium rounded mt-3">
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="order-1 md:order-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand-slate))] mb-4">01 · Identity</p>
          <h3 className="text-2xl font-semibold mb-4 text-[hsl(var(--brand-charcoal))]">Per-workspace ConnectID®</h3>
          <p className="text-[hsl(var(--brand-slate))] leading-relaxed text-lg">
            Each workspace runs its own ConnectID®-accredited identity flow. Clients verify with their own bank — no documents, no friction.
          </p>
        </div>
      </div>

      {/* ROW 2: Typed Change Requests */}
      <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="order-1">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand-slate))] mb-4">02 · Structured data</p>
          <h3 className="text-2xl font-semibold mb-4 text-[hsl(var(--brand-charcoal))]">Typed change requests</h3>
          <p className="text-[hsl(var(--brand-slate))] leading-relaxed text-lg">
            Bank, contact, address, written instructions — captured as structured fields, not screenshots of email threads.
          </p>
        </div>
        <div className="order-2 bg-white border border-[hsl(var(--brand-sand))] rounded-xl shadow-sm relative h-[420px] flex flex-col overflow-hidden">
          <div className="h-10 bg-[hsl(var(--brand-bone))] border-b border-[hsl(var(--brand-sand))] flex items-center px-4 gap-2 shrink-0">
            <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-sand))]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-sand))]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-sand))]" />
          </div>
          <div className="flex-1 p-8 overflow-hidden bg-[hsl(var(--brand-bone))] flex items-center justify-center">
            <div className="w-full max-w-sm bg-white border border-[hsl(var(--brand-sand))] rounded-lg p-6 shadow-sm">
              <h4 className="font-semibold text-lg border-b border-[hsl(var(--brand-sand))] pb-3 mb-4">
                Update bank account <span className="text-[hsl(var(--brand-slate))] font-normal">· Maya Patel</span>
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[hsl(var(--brand-slate))] mb-1.5">Account name</label>
                  <div className="h-9 px-3 border border-[hsl(var(--brand-sand))] rounded bg-[hsl(var(--brand-bone))] flex items-center text-sm">Maya Patel</div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <label className="block text-xs font-semibold text-[hsl(var(--brand-slate))] mb-1.5">BSB</label>
                    <div className="h-9 px-3 border border-[hsl(var(--brand-sand))] rounded bg-[hsl(var(--brand-bone))] flex items-center text-sm">062-000</div>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-[hsl(var(--brand-slate))] mb-1.5">Account number</label>
                    <div className="h-9 px-3 border border-[hsl(var(--brand-sand))] rounded bg-[hsl(var(--brand-bone))] flex items-center text-sm">1234 5678</div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[hsl(var(--brand-slate))] mb-1.5">Confirm account number</label>
                  <div className="h-9 px-3 border border-[hsl(var(--brand-sand))] rounded bg-[hsl(var(--brand-bone))] flex items-center text-sm">1234 5678</div>
                </div>
              </div>
              <button className="w-full py-2 bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] text-sm font-medium rounded mt-6">
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ROW 3: Audit timeline */}
      <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="order-2 md:order-1 bg-white border border-[hsl(var(--brand-sand))] rounded-xl shadow-sm relative h-[420px] flex flex-col overflow-hidden">
          <div className="h-10 bg-[hsl(var(--brand-bone))] border-b border-[hsl(var(--brand-sand))] flex items-center px-4 gap-2 shrink-0">
            <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-sand))]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-sand))]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-sand))]" />
          </div>
          <div className="flex-1 p-8 bg-[hsl(var(--brand-bone))] flex items-center justify-center">
            <div className="w-full max-w-sm bg-white border border-[hsl(var(--brand-sand))] rounded-lg p-6 shadow-sm">
              <div className="relative border-l border-[hsl(var(--brand-sand))] ml-2 space-y-6 pb-2">
                {[
                  { t: "Request created", who: " · Olivia (staff)", time: "09:40", color: "sand" },
                  { t: "Email delivered", who: "", time: "09:41", color: "sand" },
                  { t: "Link opened", who: " · client device", time: "10:15", color: "sand" },
                  { t: "ID verified", who: " · ConnectID · CBA", time: "10:18", color: "forest" },
                  { t: "Submission received", who: " · name match", time: "10:20", color: "forest" },
                  { t: "Marked complete", who: " · Daniel (staff)", time: "11:05", color: "brass" },
                ].map((ev, i) => (
                  <div key={i} className="relative pl-6">
                    <div className={`absolute w-2.5 h-2.5 rounded-full border-2 border-white -left-[5px] top-1 ${
                      ev.color === "forest" ? "bg-[hsl(var(--brand-forest))]" :
                      ev.color === "brass" ? "bg-[hsl(var(--brand-brass))]" :
                      "bg-[hsl(var(--brand-sand))]"
                    }`} />
                    <div className="flex justify-between items-start">
                      <p className="text-sm font-medium">{ev.t}<span className="text-[hsl(var(--brand-slate))] font-normal">{ev.who}</span></p>
                      <span className="text-[10px] font-mono text-[hsl(var(--brand-slate))]">{ev.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="order-1 md:order-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand-slate))] mb-4">03 · Audit</p>
          <h3 className="text-2xl font-semibold mb-4 text-[hsl(var(--brand-charcoal))]">Audit-ready trail</h3>
          <p className="text-[hsl(var(--brand-slate))] leading-relaxed text-lg">
            Every event — sent, opened, ID-verified, submitted, reviewed, completed — timestamped, attributed, immutable.
          </p>
        </div>
      </div>

      {/* ROW 4: Name-mismatch */}
      <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="order-1">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand-slate))] mb-4">04 · Safety net</p>
          <h3 className="text-2xl font-semibold mb-4 text-[hsl(var(--brand-charcoal))]">Name-mismatch warnings</h3>
          <p className="text-[hsl(var(--brand-slate))] leading-relaxed text-lg">
            We flag when the verified identity name doesn't match the request subject — before money moves.
          </p>
        </div>
        <div className="order-2 bg-white border border-[hsl(var(--brand-sand))] rounded-xl shadow-sm relative h-[420px] flex flex-col overflow-hidden">
          <div className="h-10 bg-[hsl(var(--brand-bone))] border-b border-[hsl(var(--brand-sand))] flex items-center px-4 gap-2 shrink-0">
            <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-sand))]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-sand))]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-sand))]" />
          </div>
          <div className="flex-1 p-8 bg-[hsl(var(--brand-bone))] flex items-center justify-center">
            <div className="w-full max-w-md bg-[hsl(0_60%_96%)] border border-[hsl(0_35%_45%)] rounded-lg p-5 shadow-sm">
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className="w-5 h-5 text-[hsl(0_35%_45%)] shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-[hsl(0_35%_45%)]">Name mismatch detected</h4>
                  <p className="text-sm text-[hsl(var(--brand-slate))] mt-1">Review the discrepancy before proceeding.</p>
                </div>
              </div>
              <div className="bg-white border border-[hsl(0_35%_80%)] rounded p-4 mb-5">
                <div className="flex justify-between items-center py-2 border-b border-[hsl(var(--brand-sand))]">
                  <span className="text-xs font-semibold text-[hsl(var(--brand-slate))] uppercase tracking-wider">Verified identity</span>
                  <span className="text-sm font-medium">Olivia M. Reed</span>
                </div>
                <div className="flex justify-between items-center py-2 pt-3">
                  <span className="text-xs font-semibold text-[hsl(var(--brand-slate))] uppercase tracking-wider">Submitted name</span>
                  <span className="text-sm font-medium">O. Reid</span>
                </div>
              </div>
              <div className="flex gap-3">
                <button className="flex-1 py-2 text-sm font-medium border border-[hsl(0_35%_45%)] text-[hsl(0_35%_45%)] bg-white rounded">
                  Reject submission
                </button>
                <button className="flex-1 py-2 text-sm font-medium border border-[hsl(var(--brand-slate))] text-[hsl(var(--brand-charcoal))] bg-white rounded">
                  Override with reason
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ROW 5: End data sprawl */}
      <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="order-2 md:order-1 bg-white border border-[hsl(var(--brand-sand))] rounded-xl shadow-sm relative h-[420px] flex flex-col overflow-hidden">
          <div className="h-10 bg-[hsl(var(--brand-bone))] border-b border-[hsl(var(--brand-sand))] flex items-center px-4 gap-2 shrink-0">
            <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-sand))]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-sand))]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-sand))]" />
          </div>
          <div className="flex-1 bg-[hsl(var(--brand-bone))] flex flex-col p-4 md:p-6 overflow-hidden">
            <div className="w-full h-full bg-white border border-[hsl(var(--brand-sand))] rounded-lg shadow-sm flex flex-col overflow-hidden">
              <div className="p-4 border-b border-[hsl(var(--brand-sand))] flex justify-between items-center">
                <div className="flex gap-2">
                  <span className="text-sm font-medium px-2 py-1 bg-[hsl(var(--brand-bone))] rounded">All</span>
                  <span className="text-sm px-2 py-1 text-[hsl(var(--brand-slate))]">In review</span>
                  <span className="text-sm px-2 py-1 text-[hsl(var(--brand-slate))]">Complete</span>
                </div>
                <div className="px-2.5 py-1 bg-[hsl(var(--brand-brass))]/10 border border-[hsl(var(--brand-brass))] text-[hsl(42_49%_30%)] text-xs font-semibold rounded">
                  4 awaiting review
                </div>
              </div>
              <div className="flex-1 overflow-hidden">
                <table className="w-full text-left text-sm whitespace-nowrap">
                  <thead className="bg-[hsl(var(--brand-bone))] text-[hsl(var(--brand-slate))] text-xs uppercase tracking-wider font-semibold">
                    <tr>
                      <th className="px-4 py-3 border-b border-[hsl(var(--brand-sand))]">Client</th>
                      <th className="px-4 py-3 border-b border-[hsl(var(--brand-sand))]">Type</th>
                      <th className="px-4 py-3 border-b border-[hsl(var(--brand-sand))]">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(var(--brand-sand))]">
                    {[
                      { name: "Maya Patel", type: "Bank Update", status: "Review", sCol: "text-amber-600 bg-amber-50 border-amber-200" },
                      { name: "O. Reid", type: "Contact Change", status: "Mismatch", sCol: "text-[hsl(0_35%_45%)] bg-[hsl(0_60%_96%)] border-[hsl(0_35%_45%)]" },
                      { name: "Apex Ltd", type: "Bank Update", status: "Complete", sCol: "text-emerald-700 bg-emerald-50 border-emerald-200" },
                      { name: "Sarah Jenkins", type: "Onboarding", status: "Review", sCol: "text-amber-600 bg-amber-50 border-amber-200" },
                      { name: "T. Wright", type: "Contact Change", status: "Sent", sCol: "text-[hsl(var(--brand-slate))] bg-[hsl(var(--brand-bone))] border-[hsl(var(--brand-sand))]" },
                    ].map((row, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3 font-medium">{row.name}</td>
                        <td className="px-4 py-3 text-[hsl(var(--brand-slate))]">{row.type}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide border rounded ${row.sCol}`}>
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="order-1 md:order-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand-slate))] mb-4">05 · System of record</p>
          <h3 className="text-2xl font-semibold mb-4 text-[hsl(var(--brand-charcoal))]">End data sprawl</h3>
          <p className="text-[hsl(var(--brand-slate))] leading-relaxed text-lg">
            Sensitive client details no longer live across inboxes, PDFs and shared spreadsheets. FortifyID becomes the single, encrypted, auditable system of record.
          </p>
        </div>
      </div>
    </div>
  );
}
